export type RenderCounterName =
  | 'groupVoxelsByType'
  | 'groupingSkipped'
  | 'compactExposedVoxels'
  | 'solidFullUploads'
  | 'solidDirtyRangeUploads'
  | 'solidUploadedWords'
  | 'solidUploadBytes'
  | 'solidChunkRemeshes'
  | 'solidRecordsWritten'
  | 'solidArenaGrows'
  | 'shadowRenders'
  // A1/A2 (docs/materials-perf-plan.md §4): preview inputs/events unchanged across a UI change, and
  // grouped-result objects carried over by the per-group reuse of a regroup.
  | 'previewStableHits'
  | 'groupReused'
  // D1 (docs/materials-perf-plan.md §2.5 / §4): SDF v2 workspace accounting. `sdfAllocBytes` counts
  // GPU bytes allocated by the SDF path (legacy: every frame; v2: only on workspace grows).
  | 'sdfAllocBytes'
  | 'sdfFramesSubmitted'
  | 'sdfFramesPublished'
  | 'sdfWorkspaceGrows'
  | 'sdfUploadBytes'
  | 'sdfDeferred'
  // D2 (docs/materials-perf-plan.md §2.5 / §4): mirror chunk uploads (8 KB each), member chunks whose
  // fragment changed per sync, field samples (nodes) and compute dispatches encoded.
  | 'sdfMirrorChunkUploads'
  | 'sdfChangedChunks'
  | 'sdfSamples'
  | 'sdfDispatches'
  // D3 (docs/materials-perf-plan.md §4 row D3): frames encoded with the fused kernels
  // (`edt` kernels), blur-table bytes uploaded (only when a layout's dims or P change),
  // and frames whose EDT band had to be cut to `SDF_EDT_MAX_BAND` (field inexact beyond the clamp).
  | 'sdfEdtFrames'
  | 'sdfBlurTableBytes'
  | 'sdfEdtBandClamped'
  // SDF v2 tiling (docs/blob-sdf-performance.md): plans that split a component into tiles, and tile
  // re-runs forced by a counter readback above the tile's vertex capacity (triangles were dropped).
  | 'sdfTiledComponents'
  // Tile-local hover (SdfHoverPatch.ts): patch frames run, and hover previews that went to the tiles.
  | 'sdfHoverPatchRuns'
  | 'sdfHoverPatchFallbacks'
  | 'sdfOverflowGrows'
  // C1 (docs/materials-perf-plan.md §2.4): the chunked sparse runtime, summed over its families.
  | 'sparseChunkRemeshes'
  | 'sparseRecordsWritten'
  | 'sparseArenaGrows'
  | 'sparseCompactions'
  | 'sparseFullUploads'
  | 'sparseDirtyRangeUploads'
  | 'sparseUploadedWords'
  // C4 (docs/materials-perf-plan.md §4): bucket renderers created lazily by the sparse runtime
  // (tube: one per occurring neighbour mask; road: one per variant).
  | 'sparseBucketRenderers'

export type RenderCountersSnapshot = Record<RenderCounterName, number>

const counters: RenderCountersSnapshot = {
  groupVoxelsByType: 0,
  groupingSkipped: 0,
  compactExposedVoxels: 0,
  solidFullUploads: 0,
  solidDirtyRangeUploads: 0,
  solidUploadedWords: 0,
  solidUploadBytes: 0,
  solidChunkRemeshes: 0,
  solidRecordsWritten: 0,
  solidArenaGrows: 0,
  shadowRenders: 0,
  previewStableHits: 0,
  groupReused: 0,
  sdfAllocBytes: 0,
  sdfFramesSubmitted: 0,
  sdfFramesPublished: 0,
  sdfWorkspaceGrows: 0,
  sdfUploadBytes: 0,
  sdfDeferred: 0,
  sdfMirrorChunkUploads: 0,
  sdfChangedChunks: 0,
  sdfSamples: 0,
  sdfDispatches: 0,
  sdfEdtFrames: 0,
  sdfBlurTableBytes: 0,
  sdfEdtBandClamped: 0,
  sdfTiledComponents: 0,
  sdfHoverPatchRuns: 0,
  sdfHoverPatchFallbacks: 0,
  sdfOverflowGrows: 0,
  sparseChunkRemeshes: 0,
  sparseRecordsWritten: 0,
  sparseArenaGrows: 0,
  sparseCompactions: 0,
  sparseFullUploads: 0,
  sparseDirtyRangeUploads: 0,
  sparseUploadedWords: 0,
  sparseBucketRenderers: 0,
}

export function incrementRenderCounter(name: RenderCounterName, amount = 1): void {
  counters[name] += amount
}

export function recordSolidAttributeUpload(args: { full: boolean; ranges: number; words: number }): void {
  if (args.full) {
    incrementRenderCounter('solidFullUploads')
  } else {
    incrementRenderCounter('solidDirtyRangeUploads', args.ranges)
  }
  incrementRenderCounter('solidUploadedWords', args.words)
  incrementRenderCounter('solidUploadBytes', args.words * Uint32Array.BYTES_PER_ELEMENT)
}

export function resetRenderCounters(): void {
  for (const key of Object.keys(counters) as RenderCounterName[]) {
    counters[key] = 0
  }
}

export function snapshotRenderCounters(): RenderCountersSnapshot {
  return { ...counters }
}

export function diffRenderCounters(
  before: RenderCountersSnapshot,
  after: RenderCountersSnapshot = snapshotRenderCounters(),
): RenderCountersSnapshot {
  const out = {} as RenderCountersSnapshot
  for (const key of Object.keys(counters) as RenderCounterName[]) {
    out[key] = after[key] - before[key]
  }
  return out
}
